import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UserAppService } from '../../../../services/user-app.service';
import { UserStateService } from 'src/app/services/user-state.service';
import { SubscriptionAppService } from 'src/app/services/subscription-app.service';
import { MarketplaceManagerService } from 'src/app/services/marketplace-manager.service';
import { ProductDetailsComponent } from '../product-details/product-details.component';

@Component({
  selector: 'app-home',
  template: `
    <!-- custom homepage (configured from producer side) -->
    <div class="iframe-container" *ngIf="htmlContent">
      <iframe
        [srcdoc]="htmlContent"
        style="width: 100%; height: 100vh; border: none;"
      ></iframe>
    </div>

    <!-- default consumer dashboard -->
    <div class="home-page" *ngIf="!htmlContent">
      <div class="greet">
        <h1>Welcome back, {{ firstName }} 👋</h1>
        <p>Configure and run your subscribed solutions, or explore new ones in the marketplace.</p>
      </div>

      <!-- Your subscriptions -->
      <div class="sec-head">
        <h2>Your subscriptions</h2>
        <a
          class="link"
          *ngIf="subscriptions?.length"
          routerLink="/home/customer/manage-solutions"
          >Manage all →</a
        >
      </div>

      <div class="sub-list" *ngIf="subscriptions?.length; else noSubs">
        <app-subscription-card *ngFor="let sub of subscriptions" [sub]="sub"></app-subscription-card>
      </div>

      <ng-template #noSubs>
        <div class="empty">
          <div class="ei">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
          </div>
          <h3>No subscriptions yet</h3>
          <p>Browse the marketplace and subscribe to a solution to start running it.</p>
          <button class="btn-primary" routerLink="/home/customer/available-solutions">
            Explore the marketplace
          </button>
        </div>
      </ng-template>

      <!-- Explore the marketplace -->
      <div class="sec-head">
        <h2>Explore the marketplace</h2>
        <a
          class="link"
          *ngIf="marketCards?.length"
          routerLink="/home/customer/available-solutions"
          >Browse all →</a
        >
      </div>

      <div class="mkt-grid" *ngIf="marketCards?.length; else noMkt">
        <div class="mkt-card" *ngFor="let card of marketCards">
          <div class="mkt-top">
            <div class="mkt-ico">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
                   stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <div>
              <div class="mkt-nm">{{ card.solution['solution_name'] | textpretty | titlecase }}</div>
              <span class="mkt-badge b-new" *ngIf="card.version['version_name']"
                >{{ card.version['version_name'] }}</span
              >
            </div>
          </div>
          <div class="mkt-desc">{{ mktDesc(card.solution) }}</div>
          <div class="mkt-foot">
            <button class="btn-primary" (click)="openMarketplace(card)">View &amp; subscribe</button>
          </div>
        </div>
      </div>

      <ng-template #noMkt>
        <div class="empty sm">
          <p>No solutions are available to you yet.</p>
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
  htmlContent: SafeHtml | undefined;
  firstName: string = 'there';
  subscriptions: any[] = [];
  marketCards: any[] = [];
  private marketSolutions: any[] = [];

  constructor(
    private userServices: UserAppService,
    private sanitizer: DomSanitizer,
    private userState: UserStateService,
    private subscriptionServices: SubscriptionAppService,
    private marketplaceServices: MarketplaceManagerService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const info: any = this.userState.getCurrentUserInfo();
    this.firstName =
      info?.first_name ||
      (info?.username || info?.email || 'there').split('@')[0];

    this.userServices.getHomepage().subscribe({
      next: (response: any) => {
        const homepageHtml = response?.homepage_html_content;
        if (homepageHtml) {
          this.htmlContent =
            this.sanitizer.bypassSecurityTrustHtml(homepageHtml);
        } else {
          this.loadDashboard();
        }
      },
      error: (error: any) => {
        console.error('an error occurred while loading homepage', error);
        this.loadDashboard();
      },
    });
  }

  loadDashboard(): void {
    this.subscriptionServices.getSubscribedSolutions().subscribe({
      next: (response: any) => {
        this.subscriptions = response?.['msg'] || [];
      },
      error: (error: any) => {
        console.error('an error occurred fetching subscriptions', error);
      },
    });

    this.marketplaceServices.getSolutionInMarket().subscribe({
      next: (response: any) => {
        this.marketSolutions = response?.['msg'] || [];
        this.loadMarketVersions();
      },
      error: (error: any) => {
        console.error('an error occurred fetching marketplace', error);
      },
    });
  }

  loadMarketVersions(): void {
    this.marketplaceServices.getVersionInMarket().subscribe({
      next: (response: any) => {
        const latest = this.getLatestVersion(response?.['msg'] || []);
        this.marketCards = latest
          .map((version: any) => ({
            version,
            solution: this.keyValueExists(
              'solution_id',
              version['solution_id'],
              this.marketSolutions
            ),
          }))
          .filter((card: any) => !!card.solution);
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  getLatestVersion(data: any[]): any[] {
    const groupedData = new Map<any, any[]>();
    for (const item of data) {
      const solutionId = item.solution_id;
      if (!groupedData.has(solutionId)) groupedData.set(solutionId, [item]);
      else groupedData.get(solutionId)!.push(item);
    }
    const latestVersions: any[] = [];
    for (const [, group] of groupedData) {
      group.sort(
        (a: any, b: any) =>
          parseInt(b.version_name.substr(1)) -
          parseInt(a.version_name.substr(1))
      );
      latestVersions.push(group[0]);
    }
    return latestVersions;
  }

  keyValueExists(key: string, value: any, data: any): any | boolean {
    const result = (data || []).find((item: any) => item[key] === value);
    return result || false;
  }

  mktDesc(solution: any): string {
    let desc = solution?.['user_facing_solution_desc'];
    if (typeof desc === 'string') {
      try {
        desc = JSON.parse(desc);
      } catch {
        return desc;
      }
    }
    return (
      desc?.subtitle ||
      desc?.description ||
      'View the details to see what this solution does and how to subscribe.'
    );
  }

  openMarketplace(card: any): void {
    this.dialog.open(ProductDetailsComponent, {
      data: { soln: card.solution, versn: card.version },
      panelClass: 'd2c-rounded-dialog',
      disableClose: false,
    });
  }
}
